/* global TrelloPowerUp */
const { getTargetOrigin } = require('./util.js');

if (typeof $ === 'undefined') {
  window.ButlerPowerUp = {
    init: () => {
      console.log('[ERROR] Could not load Butler - jQuery not available.');
      if (typeof TrelloPowerUp !== 'undefined') {
        TrelloPowerUp.initialize(
          {
            'board-buttons': () => [],
            'card-buttons': () => [],
            'show-settings': () => [],
            'remove-data': () => [],
          },
          {
            targetOrigin: getTargetOrigin(),
          }
        );
      }
    },
  };
  throw new Error(`jQuery is undefined`);
}

// setup polyfills
require('core-js/stable');

const Sentry = require('@sentry/browser');
const LZString = require('lz-string');
const Analytics = require('./include/analytics.js');
const Log = require('./include/logging.js');
const util = require('./include/util.js');
const CommandStorage = require('./include/powerup-command-storage.js');
const Suggestions = require('./include/powerup-suggestions.js');
const Plan = require('./include/powerup-plan.js');
const TrelloApi = require('./include/trello-api.js');
const { RegistrationError } = require('./include/trello-api-error.js');

// These imported values weren't being used, but I'm not sure if they might have
// side effects. Someone should take a look through these at another time to
// determine whether this is the appropriate entry point for them.
require('./include/api-endpoint.js');
require('./include/builder-util.js');
require('./include/powerup-tabs.js');
require('./include/powerup-command-log.js');
require('./include/powerup-message-broker.js');
require('./include/powerup-command-runner.js');
require('./include/powerup-dashboard.js');
require('./include/powerup-account.js');
require('./include/powerup-sentry-initializer.js');
require('./include/initGlobalTheme.js');

Log.init();
const ButlerPowerUp = {};
const { Auth } = TrelloApi;

(function() {
  ButlerPowerUp.init = function() {
    if (typeof TrelloPowerUp === 'undefined') {
      Sentry.captureException(new Error('TrelloPowerUp script failed to load.'));
      return console.log('[ERROR] TrelloPowerUp script failed to load.');
    }

    window._trello = TrelloPowerUp.initialize(
      {
        'board-buttons': getBoardButtons,
        'card-buttons': getCardButtons,
        'show-settings': showDashboard,
        'remove-data': removeData,
      },
      {
        targetOrigin: getTargetOrigin(),
      }
    );
    const t = window._trello;

    t.member('id')
      .then(function(m) {
        Sentry.configureScope(function(scope) {
          if (m && m.id !== 'notLoggedIn') {
            scope.setUser({ id: m.id });
          }
        });
      })
      .catch(err =>
        util.handleError(new Error(`configureSentry: ${err.message}`), 'configureSentry')
      );
  };

  function getBoardButtons(t) {
    // We are gating breadcrumbs behind a feature flag
    // and need access to t at the earliest available point.
    if (!window.isSentryInitialized) {
      window.SentryInitializer.init(t);
    }

    if (!t.isMemberSignedIn() || !t.memberCanWriteToModel('board')) {
      return [];
    }

    CommandStorage.init(t);

    return CommandStorage.getLocalCommands()
      .then(function(localCommands) {
        const buttons = localCommands.commands;

        const enabledButtons = buttons.filter(
          ({ enabled, type }) => enabled && type === 'board-button'
        );

        if (enabledButtons.length > 0) {
          return t.board('members').then(function(boardInfo) {
            if (!boardInfo.members.find(({ id }) => id === t.getContext().member)) {
              return [];
            }

            return enabledButtons
              .sort(function(a, b) {
                return a.is_own ? (b.is_own ? 0 : -1) : b.is_own ? 1 : 0;
              })
              .slice(0, 20)
              .map(function(button) {
                return {
                  icon: `./assets/fa-5.1.1/icons/white/${button.image}.svg`,
                  text: button.label,
                  callback(cb_t) {
                    cb_t
                      .boardBar({
                        height: 38,
                        title: 'Automation',
                        url: `./powerup-command-runner.html?cmd=${button.id}&type=boardButton`,
                      })
                      .catch(err =>
                        util.handleError(
                          new Error(`boardButtonClick: ${err.message}`),
                          'boardButtonClick'
                        )
                      );
                  },
                };
              });
          });
        }

        return [];
      })
      .catch(err => {
        util.handleError(new Error(`getBoardButtons: ${err.message}`), 'getBoardButtons');
        return [];
      });
  }

  function getCardButtons(t) {
    if (
      t.getContext().omitCardButtons ||
      !t.isMemberSignedIn() ||
      !t.memberCanWriteToModel('card')
    ) {
      return [];
    }

    CommandStorage.init(t);
    Plan.checkUserQuota(t).catch(err => {
      util.handleError(
        new Error(`getCardButtons-checkUserQuota: ${err.message}`),
        'getCardButtons-checkUserQuota'
      );
    });

    return TrelloPowerUp.Promise.all([Plan.getUserPlanLocal(t), CommandStorage.getLocalCommands()])
      .spread(function(plan, localCommands) {
        const buttons = localCommands.commands.filter(function(button) {
          return button.type === 'card-button' && button.enabled;
        });

        const limitedButtons = buttons
          .sort(function(a, b) {
            return a.is_own ? (b.is_own ? 0 : -1) : b.is_own ? 1 : 0;
          })
          .slice(0, 20)
          .map(function(button) {
            return {
              icon: `./assets/fa-5.1.1/icons/grey/${button.image}.svg`,
              text: button.label,
              callback(cb_t) {
                Analytics.sendUIEvent({
                  action: 'clicked',
                  actionSubject: 'button',
                  actionSubjectId: 'butlerCardButton',
                  source: 'cardDetailScreen',
                  attributes: {
                    close: button.close,
                    section: 'powerupsSection',
                  },
                });
                cb_t.boardBar({
                  height: 38,
                  title: 'Automation',
                  url: `./powerup-command-runner.html?cmd=${button.id}`,
                });
                if (button.close) {
                  cb_t.hideCard().catch(function() {
                    // noop
                  });
                }
              },
            };
          });
        return limitedButtons;
      })
      .catch(err => {
        util.handleError('getCardButtons', err);
        return [];
      });
  }

  function showDashboard(t, opts) {
    t.closePopup().catch(() => {});
    if ('canShowButlerUI' in t.getContext() && !t.getContext().canShowButlerUI) {
      return t.alert({
        message: 'Your enterprise has restricted the Butler dashboard to workspace admins.',
        duration: 10,
      });
    }
    if (!t.isMemberSignedIn() || !t.memberCanWriteToModel('board')) {
      return t.alert({
        display: 'warning',
        duration: 10,
        message: 'To use Butler on this board you must be a member of the board.',
      });
    }
    CommandStorage.init(t);
    let encodedNewCommand;
    const params = {
      board: opts.context.board,
    };
    const newCommandParams = new URLSearchParams();
    if (opts.butlerTab) {
      params.tab = opts.butlerTab;
    }
    if (opts.butlerCmdCopy) {
      params.commandCopy = opts.butlerCmdCopy;
    }
    if (opts.butlerCmdEdit) {
      params.commandEdit = opts.butlerCmdEdit;
    }
    if (opts.butlerCmdLog) {
      params.commandLog = opts.butlerCmdLog;
    }
    if (opts.newCommand) {
      // ensure we've got an lz-compressed string
      try {
        const decodedCommand = LZString.decompressFromEncodedURIComponent(opts.newCommand);
        if (!decodedCommand) {
          encodedNewCommand = LZString.compressToEncodedURIComponent(opts.newCommand);
        } else {
          encodedNewCommand = opts.newCommand;
        }
        params.newCommand = encodedNewCommand;
        newCommandParams.set('c', encodedNewCommand);
      } catch (e) {
        encodedNewCommand = null;
      }
    }
    if (opts.butlerUsage) {
      params.usage = opts.butlerUsage;
    }
    if (opts.butlerAccount) {
      params.account = opts.butlerAccount;
    }
    if (opts.newIcon) {
      params.newIcon = opts.newIcon;
      newCommandParams.set('i', opts.newIcon);
    }
    if (opts.newLabel) {
      params.newLabel = opts.newLabel;
      newCommandParams.set('l', opts.newLabel);
    }
    if (opts.highlightCommand) {
      params.highlightCommand = opts.highlightCommand;
    }
    if (opts.obo) {
      params.obo = opts.obo;
    }
    const queryParams = new URLSearchParams(params);
    // if we're using the new button, then the url will already be set correctly by web
    // all we need to do is return the signed iframe url
    const htmlFileName = opts.modernization
      ? 'powerup-dashboard-modernization.html'
      : 'powerup-dashboard.html';
    const retUrl = TrelloPowerUp.util.relativeUrl(`./${htmlFileName}?${queryParams.toString()}`);
    const signed = t.signUrl(retUrl, { inset: true });
    return signed;
  }

  function removeData(t) {
    Auth.clearMemory(t);
    return true;
  }
})();

window.ButlerPowerUp = ButlerPowerUp;
